import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Table, Spin, Typography } from 'antd';
import { 
  ProjectOutlined, 
  RiseOutlined, 
  DollarOutlined, 
  WarningOutlined 
} from '@ant-design/icons';
import { Pie, Line } from '@ant-design/charts';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';

const { Title } = Typography;

interface DashboardData {
  kpis: {
    total_projects: number;
    new_projects_this_month: number;
    avg_cost: number;
    pending_approvals: number;
  };
  costStructure: any;
  costTrend: any[];
  topProjects: any[];
}

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [kpis, structure, trend, ranking] = await Promise.all([
        api.get('/dashboard/kpis') as any,
        api.get('/dashboard/cost-structure') as any,
        api.get('/dashboard/cost-trend') as any,
        api.get('/dashboard/top-ranking?top=10') as any
      ]);
      
      setData({
        kpis: kpis.data,
        costStructure: structure.data,
        costTrend: trend.data,
        topProjects: ranking.data
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  // Prepare cost structure data for pie chart
  const pieData = data?.costStructure ? [
    { type: t('dashboard.laborCost'), value: data.costStructure.labor_cost || 0 },
    { type: t('dashboard.burdenCost'), value: data.costStructure.burden_cost || 0 },
    { type: t('dashboard.materialCost'), value: data.costStructure.material_cost || 0 },
    { type: t('dashboard.toolingCost'), value: data.costStructure.tooling_cost || 0 },
    { type: t('dashboard.equipmentCost'), value: data.costStructure.equipment_cost || 0 },
    { type: t('dashboard.sgaCost'), value: data.costStructure.sga_cost || 0 },
    { type: t('dashboard.idlCost'), value: data.costStructure.idl_cost || 0 },
    { type: t('dashboard.scrapCost'), value: data.costStructure.scrap_cost || 0 }
  ].filter(d => d.value > 0) : [];

  const pieConfig = {
    data: pieData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    innerRadius: 0.6,
    label: {
      type: 'inner',
      offset: '-50%',
      content: '{value}',
      style: { textAlign: 'center', fontSize: 12 }
    },
    legend: { position: 'right' }
  };

  // Prepare trend data for line chart
  const lineData = data?.costTrend?.map(d => ({
    month: d.month,
    value: d.total_cost,
    category: t('dashboard.totalCost')
  })) || [];

  const lineConfig = {
    data: lineData,
    xField: 'month',
    yField: 'value',
    seriesField: 'category',
    smooth: true,
    point: { size: 4, shape: 'circle' },
    yAxis: { label: { formatter: (v: string) => `$${Number(v).toLocaleString()}` } }
  };

  // Top projects table columns
  const columns = [
    {
      title: t('project.projectNo'),
      dataIndex: 'project_no',
      key: 'project_no'
    },
    {
      title: t('project.name'),
      dataIndex: i18n.language === 'zh' ? 'project_name' : 'project_name_en',
      key: 'name'
    },
    {
      title: t('dashboard.totalCost'),
      dataIndex: 'total_cost',
      key: 'total_cost',
      render: (v: number) => `$${v?.toLocaleString() || 0}`
    },
    {
      title: t('dashboard.laborCost'),
      dataIndex: 'labor_cost',
      key: 'labor_cost',
      render: (v: number) => `$${v?.toLocaleString() || 0}`
    },
    {
      title: t('dashboard.materialCost'),
      dataIndex: 'material_cost',
      key: 'material_cost',
      render: (v: number) => `$${v?.toLocaleString() || 0}`
    }
  ];

  return (
    <div>
      <Title level={4}>{t('menu.dashboard')}</Title>
      
      {/* KPI Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('dashboard.totalProjects')}
              value={data?.kpis?.total_projects || 0}
              prefix={<ProjectOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('dashboard.newThisMonth')}
              value={data?.kpis?.new_projects_this_month || 0}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('dashboard.avgCost')}
              value={data?.kpis?.avg_cost || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('dashboard.pendingApprovals')}
              value={data?.kpis?.pending_approvals || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>
      
      {/* Charts */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title={t('dashboard.costStructure')}>
            {pieData.length > 0 ? <Pie {...pieConfig} /> : <div style={{ textAlign: 'center', padding: 50 }}>{t('common.noData')}</div>}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t('dashboard.costTrend')}>
            {lineData.length > 0 ? <Line {...lineConfig} /> : <div style={{ textAlign: 'center', padding: 50 }}>{t('common.noData')}</div>}
          </Card>
        </Col>
      </Row>
      
      {/* Top Projects */}
      <Card title={t('dashboard.topProjects')}>
        <Table 
          dataSource={data?.topProjects || []}
          columns={columns}
          rowKey="cost_id"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default Dashboard;